import { Fragment, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { ShopTuningShopHint } from '~/components/ui/ShopTuningShopHint';
import { useZoomFactor } from '~/hooks';
import {
	TechTuningCategory,
	TechTuningItemProperty,
	TechTuningRarity,
} from '~/shared';
import { CategoryItemImages } from '../CategoryTab';
import {
	ColorGradient,
	ItemColor,
	TuningShopRarityGradientColors,
} from '../ItemColor';
import { StyledWrapper } from './CaseItemColor.styled';

const CaseItemColor = ({
	category,
	color,
	name,
	properties,
}: {
	category: TechTuningCategory;
	color: ColorGradient;
	name: string;
	properties?: Record<TechTuningItemProperty, number | null>;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'name'>) => {
	const [hintVisible, setHintVisible] = useState(false);
	const [hintPosition, setHintPosition] = useState<{
		top: number;
		left: number;
	}>({ top: 0, left: 0 });
	const containerRef = useRef<HTMLDivElement>(null);
	const zoomFactor = useZoomFactor();

	const showHint = () => {
		if (containerRef.current) {
			const rect = containerRef.current.getBoundingClientRect();
			setHintPosition({
				top: rect.bottom * (1 / zoomFactor) + 10,
				left:
					rect.left * (1 / zoomFactor) + (rect.width * (1 / zoomFactor)) / 2,
			});
		}
		setHintVisible(true);
	};

	const hideHint = () => {
		setHintVisible(false);
	};

	return (
		<Fragment>
			{createPortal(
				<CSSTransition
					in={hintVisible}
					classNames={'hint'}
					timeout={300}
					unmountOnExit
				>
					<ShopTuningShopHint
						name={name}
						properties={properties}
						style={{
							top: hintPosition.top,
							left: hintPosition.left,
							position: 'fixed',
							transform: 'translateX(-50%)',
						}}
					/>
				</CSSTransition>,
				document.body,
			)}
			<StyledWrapper
				ref={containerRef}
				onMouseEnter={showHint}
				onMouseLeave={hideHint}
			>
				<ItemColor
					image={
						CategoryItemImages[category ?? TechTuningCategory.ENGINE] || ''
					}
					size={110}
					imageSize={80}
					color={
						color || TuningShopRarityGradientColors[TechTuningRarity.COMMON]
					}
					style={{ borderRadius: 10 }}
				/>
			</StyledWrapper>
		</Fragment>
	);
};

export { CaseItemColor };
